import React, { useEffect, useState, useRef } from 'react'
import DataTable from 'react-data-table-component'
import Select from 'react-select'
import { FiMenu } from 'react-icons/fi'
import { getAllData } from '../../lib/collegelistinfo'
import { addData, getStudentIdData, deleteIdData } from '../../lib/studentcollegeinfo'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { stateOptions, columnTitles, getStudentCollegeColumns, stateSelectStyles } from '../../Data/CollegeManagement/StudentCollegeListData'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'

const CollegeList = ({ studentId }) => {
	const [collegelistinfo, setCollegeListInfo] = useState([])
	const [originalStudentCollegeList, setOriginalStudentCollegeList] = useState([]) // Original DB state
	const [prevStudentCollegeList, setStudentCollegeList] = useState([]) // Current UI selections
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const [activeTab, setActiveTab] = useState('global')
	const [selectedStates, setSelectedStates] = useState([])
	const [openColumnMenu, setOpenColumnMenu] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const { user } = useAuth()
	const effectiveStudentId = studentId || user?.id
	const columnMenuRef = useRef(null)

	// Column visibility state (Hide Unit ID by default)
	const [visibleColumns, setVisibleColumns] = useState(columnTitles.map((_, index) => index !== 0))

	const getData = async () => {
		try {
			setLoading(true)
			setError('')
			const [list, list2] = await Promise.all([getAllData(setLoading), getStudentIdData(effectiveStudentId)])
			setCollegeListInfo(list)
			setStudentCollegeList(list2) // Current UI selections
			setOriginalStudentCollegeList(list2) // Keep original DB state
		} catch (error) {
			console.error('Error fetching college data:', error)
			setError('Failed to load college data')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (effectiveStudentId) {
			getData()
		}
	}, [effectiveStudentId])

	// Handle clicks outside column menu
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (columnMenuRef.current && !columnMenuRef.current.contains(event.target) && !event.target.classList.contains('column_menu')) {
				setOpenColumnMenu(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	const handleUpdate = async () => {
		try {
			setLoading(true)

			// Get original DB college IDs
			const originalCollegeIds = originalStudentCollegeList.map((college) => college.collegeId)

			// Get current UI selected college IDs
			const currentCollegeIds = prevStudentCollegeList.map((college) => college.collegeId)

			// Find colleges to add (selected in UI but not in original DB)
			const collegeIdsToAdd = currentCollegeIds.filter((id) => !originalCollegeIds.includes(id))

			// Find colleges to delete (in original DB but not selected in UI)
			const recordsToDelete = originalStudentCollegeList.filter((college) => !currentCollegeIds.includes(college.collegeId))

			// Add new college selections
			if (collegeIdsToAdd.length > 0) {
				await addData(
					collegeIdsToAdd.map((collegeId) => ({
						collegeId: String(collegeId),
						studentId: effectiveStudentId
					}))
				)
			}

			// Delete removed college selections
			if (recordsToDelete.length > 0) {
				await Promise.all(
					recordsToDelete.map(async (record) => {
						await deleteIdData(record.id) // Delete by the record ID, not college ID
					})
				)
			}

			showToastMessage('College list updated successfully!')
			await getData() // Refresh data and reset original state
		} catch (error) {
			console.error('Error updating college data:', error)
			setError('Failed to update college data')
		} finally {
			setLoading(false)
		}
	}

	// Filter data based on selected states and search query
	const filteredColleges = collegelistinfo.filter((college) => {
		const stateMatch = selectedStates.length === 0 || selectedStates.includes(college.state)

		// Search across multiple fields
		const searchMatch =
			!searchQuery ||
			(college.unitid && college.unitid.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.college && college.college.toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.state && college.state.toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.city && college.city.toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.type && college.type.toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.population && college.population.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.us_news_rank && college.us_news_rank.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.grd_rate && college.grd_rate.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.rate && college.rate.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.gpa_avg && college.gpa_avg.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.sat_english && college.sat_english.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.sat_math && college.sat_math.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.sat_total && college.sat_total.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.in_state_tuition && college.in_state_tuition.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.out_state_tuition && college.out_state_tuition.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.url && college.url.toLowerCase().includes(searchQuery.toLowerCase())) ||
			(college.net_price_calc_url && college.net_price_calc_url.toLowerCase().includes(searchQuery.toLowerCase()))

		return stateMatch && searchMatch
	})

	// Get "My Colleges" data
	const myColleges = filteredColleges.filter((college) => prevStudentCollegeList.some((prevCollege) => prevCollege.collegeId === college.id))

	// Get current data based on active tab
	const currentData = activeTab === 'my' ? myColleges : filteredColleges

	// Get columns with current state
	const columns = getStudentCollegeColumns(prevStudentCollegeList, setStudentCollegeList, effectiveStudentId)

	// Filter columns based on visibility
	const visibleColumnsData = columns.filter((_, index) => visibleColumns[index])


	return (
		<div className="card mb-4 shadow-sm" style={{ borderRadius: '8px' }}>
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<div className="card-header border-bottom d-flex justify-content-between align-items-center">
				<div className="d-flex align-items-center">
					<button
						type="button"
						className="btn btn-link p-0 me-2"
						data-bs-toggle="collapse"
						data-bs-target="#collapseCollegeList"
						aria-expanded="false"
						aria-controls="collapseCollegeList"
					>
						<i className="fa fa-chevron-down"></i>
					</button>
					<h5 className="mb-0 d-inline-block">College List</h5>
				</div>
			</div>
			<div className="collapse" id="collapseCollegeList">
				<div className="card-body">
					{error && (
						<div className="alert alert-danger alert-dismissible fade show" role="alert">
							<strong>Error:</strong> {error}
							<button type="button" className="btn-close" onClick={() => setError('')} aria-label="Close"></button>
						</div>
					)}

					{/* Tab Navigation */}
					<ul className="nav nav-tabs mb-3">
						<li className="nav-item">
							<button className={`nav-link ${activeTab === 'global' ? 'active' : ''}`} onClick={() => setActiveTab('global')} type="button">
								All College
							</button>
						</li>
						<li className="nav-item">
							<button className={`nav-link ${activeTab === 'my' ? 'active' : ''}`} onClick={() => setActiveTab('my')} type="button">
								My Colleges
							</button>
						</li>
					</ul>

					{/* Filter Controls */}
					<div className="mb-3">
						{/* First Row: Column Menu and State Select on Left, Search on Right */}
						<div className="d-flex justify-content-between align-items-center mb-3">
							{/* Left Side: Column Menu + State Select */}
							<div className="d-flex align-items-center gap-3">
								{/* Column Visibility Menu */}
								<div className="position-relative">
									<FiMenu
										className="column_menu"
										onClick={() => setOpenColumnMenu(!openColumnMenu)}
										style={{ cursor: 'pointer', fontSize: '20px' }}
									/>
									{openColumnMenu && (
										<div
											className="column-toggle-checkboxes position-absolute border rounded shadow p-3"
											ref={columnMenuRef}
											style={{ top: '100%', left: 0, zIndex: 1000, minWidth: '200px' }}
										>
											{columnTitles.map((title, index) => (
												<div key={index} className="form-check">
													<input
														className="checkbox_animated"
														type="checkbox"
														id={`col-${index}`}
														checked={visibleColumns[index]}
														onChange={() => {
															const newVisibleColumns = [...visibleColumns]
															newVisibleColumns[index] = !newVisibleColumns[index]
															setVisibleColumns(newVisibleColumns)
														}}
													/>
													<label className="form-check-label" htmlFor={`col-${index}`}>
														{title}
													</label>
												</div>
											))}
										</div>
									)}
								</div>

								{/* State Filter */}
								<div style={{ width: '300px' }}>
									<Select
										isMulti
										options={stateOptions}
										className="state-select"
										placeholder="Select states..."
										onChange={(selectedOptions) => {
											const selectedValues = selectedOptions ? selectedOptions.map((option) => option.value) : []
											setSelectedStates(selectedValues)
										}}
										styles={stateSelectStyles}
									/>
								</div>
							</div>

							{/* Right Side: Search Input */}
							<input
								type="text"
								className="form-control"
								placeholder="Search..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								style={{ width: '300px' }}
							/>
						</div>
					</div>

					{loading ? (
						<div className="text-center">
							<div className="spinner-border" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
							<p className="mt-2">Loading college data...</p>
						</div>
					) : (
						<>
							<div className="table-responsive">
								<DataTable
									columns={visibleColumnsData}
									data={currentData}
									striped={true}
									center={true}
									pagination
									paginationPerPage={10}
									paginationRowsPerPageOptions={[10, 25, 50, 100]}
									highlightOnHover={true}
									responsive={true}
									noDataComponent="No colleges found"
									customStyles={{
										headCells: {
											style: {
												fontWeight: 'bold',
												fontSize: '11px',
												padding: '12px 8px',
												minHeight: '60px',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												color: '#000'
											}
										},
										rows: {
											style: {
												color: '#000'
											}
										},
										cells: {
											style: {
												fontSize: '12px',
												padding: '8px',
												color: '#000'
											}
										}
									}}
								/>
							</div>

							<div className="d-flex justify-content-between mt-3">
								<div></div>
								<button onClick={handleUpdate} className="btn btn-primary" disabled={loading}>
									<i className="fa fa-save me-2"></i>
									{loading ? 'Updating...' : 'UPDATE'}
								</button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	)
}

export { CollegeList }
